import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ShoppingCart, 
  Calendar, 
  Heart, 
  ArrowRight, 
  Utensils, 
  Clock, 
  Users,
  Sparkles,
  ChevronRight,
  DollarSign,
  Dumbbell,
  Timer,
  CheckCircle2,
  PlayCircle,
  Loader2
} from 'lucide-react';
import MobileLanding from '@/components/MobileLanding';
import { PreSignupTeaser, PathCards, PATH_DETAILS } from '@/components/OnboardingFlow';

const Landing = () => {
  const navigate = useNavigate();
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showTeaser, setShowTeaser] = useState(false);
  
  // Returning user state
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [returningUserData, setReturningUserData] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check for returning user on mount
  useEffect(() => {
    const checkReturningUser = async () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        // Check if they've visited before (anonymous returning visitor)
        const hasVisited = localStorage.getItem('carted_visited');
        const lastPath = localStorage.getItem('carted_last_path');
        
        if (hasVisited && lastPath) {
          setIsReturningUser(true);
          setReturningUserData({
            type: 'anonymous',
            lastPath: lastPath,
            pathDetails: PATH_DETAILS[lastPath]
          });
        }
        setLoadingUser(false);
        return;
      }

      // Authenticated user - fetch their data
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const [prefsRes, planRes] = await Promise.all([
          fetch(`${API_URL}/api/user/preferences`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/meal-plans`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        if (prefsRes.ok && planRes.ok) {
          const prefs = await prefsRes.json();
          const plan = await planRes.json();
          
          // Count meals in plan
          let mealCount = 0;
          if (plan?.days) {
            mealCount = plan.days.reduce((acc, day) => acc + (day.meals?.length || 0), 0);
          }

          // Use path from meal plan first (actual current path), then preferences
          const pathId = plan?.path_id || prefs.recommended_path || 'weeknight-healthy';
          const pathName = plan?.path_name || PATH_DETAILS[pathId]?.name;
          
          setIsReturningUser(true);
          setReturningUserData({
            type: 'authenticated',
            pathId: pathId,
            pathDetails: PATH_DETAILS[pathId],
            mealCount: mealCount,
            pathName: pathName
          });
        }
      } catch (error) {
        console.error('Failed to check returning user:', error);
      }
      setLoadingUser(false);
    };

    checkReturningUser();
    
    // Mark as visited for future anonymous returns
    localStorage.setItem('carted_visited', 'true');
  }, []);

  // Handle teaser completion - navigate to auth with prefilled goal
  const handleTeaserSignup = (goal, pathId) => {
    // Store in sessionStorage for post-signup onboarding
    sessionStorage.setItem('onboarding_goal', goal);
    sessionStorage.setItem('recommended_path', pathId);
    localStorage.setItem('carted_last_path', pathId); // Remember for anonymous returns
    navigate('/auth?signup=true');
  };

  // Handle direct path selection from cards
  const handlePathSelect = (pathId) => {
    sessionStorage.setItem('recommended_path', pathId);
    localStorage.setItem('carted_last_path', pathId);
    navigate('/auth?signup=true&path=' + pathId);
  };

  // Handle continue for returning user
  const handleContinue = () => {
    if (returningUserData?.type === 'authenticated') {
      navigate('/meal-planner');
    } else {
      navigate('/auth?signup=true');
    }
  };

  // Render mobile version
  if (isMobile) {
    return <MobileLanding />;
  }

  const benefits = [
    { icon: CheckCircle2, text: 'Pick a weekly meal style (family-friendly, healthy, fast)' },
    { icon: CheckCircle2, text: 'Get a flexible dinner plan automatically' },
    { icon: CheckCircle2, text: 'Build a complete grocery cart in one tap' },
  ];

  const stats = [
    { value: '50K+', label: 'Recipes' },
    { value: '2 min', label: 'To Plan a Week' },
    { value: '30%', label: 'Less Food Waste' }
  ];

  // Path preview data
  const pathPreviews = [
    { id: 'weeknight-healthy', name: 'Weeknight Healthy', icon: Heart, color: 'bg-green-500', time: '30 min', desc: 'Balanced meals, fast' },
    { id: 'family-friendly', name: 'Family Friendly', icon: Users, color: 'bg-purple-500', time: '35 min', desc: 'Kid-approved' },
    { id: 'high-protein', name: 'High Protein', icon: Dumbbell, color: 'bg-red-500', time: '35 min', desc: 'Protein-forward meals' },
    { id: 'low-prep', name: 'Low Prep', icon: Timer, color: 'bg-blue-500', time: '20 min', desc: 'Minimal effort' },
    { id: 'budget-friendly', name: 'Budget Friendly', icon: DollarSign, color: 'bg-amber-500', time: '30 min', desc: 'Save money' },
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F0]" data-testid="landing-page">
      {/* Hero Section - Path First */}
      <section className="relative overflow-hidden">
        {/* Navigation */}
        <nav className="absolute top-0 left-0 right-0 z-10 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto py-6 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-8 w-8 text-[#4A5D4A]" />
              <span className="text-xl font-semibold text-[#2D3A2D]">Carted</span>
            </div>
            <Link to="/auth">
              <Button variant="outline" className="rounded-full border-[#4A5D4A] text-[#4A5D4A] hover:bg-[#4A5D4A] hover:text-white" data-testid="hero-signin">
                Sign In
              </Button>
            </Link>
          </div>
        </nav>

        {/* Hero Content - Simplified, Path-First */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16 md:pt-40 md:pb-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Value Prop or Welcome Back */}
            <div className="animate-fade-in">
              {/* Returning User Welcome */}
              {!loadingUser && isReturningUser && returningUserData ? (
                <>
                  <Badge variant="default" className="mb-4 bg-[#4A5D4A]/20 text-[#4A5D4A] border-[#4A5D4A]/30">
                    <PlayCircle className="h-3 w-3 mr-1" />
                    Welcome back!
                  </Badge>
                  <h1 className="text-4xl md:text-5xl font-semibold tracking-tight leading-[1.1] text-foreground mb-4">
                    {returningUserData.type === 'authenticated' ? (
                      <>
                        Continue your<br />
                        <span className="text-primary">{returningUserData.pathName || returningUserData.pathDetails?.name}</span>
                        <br />plan
                      </>
                    ) : (
                      <>
                        Pick up where<br />
                        you <span className="text-primary">left off</span>
                      </>
                    )}
                  </h1>
                  <p className="text-lg text-muted-foreground leading-relaxed mb-6 max-w-lg">
                    {returningUserData.type === 'authenticated' ? (
                      returningUserData.mealCount > 0 
                        ? `You have ${returningUserData.mealCount} meals planned. Ready to shop?`
                        : `Your ${returningUserData.pathDetails?.name} path is waiting. Let's build your week.`
                    ) : (
                      `You were interested in ${returningUserData.pathDetails?.name}. Ready to get your personalized meal plan?`
                    )}
                  </p>
                  
                  {/* Returning User CTA */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button 
                      size="lg" 
                      className="rounded-full h-14 px-8 text-lg btn-lift group"
                      onClick={handleContinue}
                      data-testid="hero-continue"
                    >
                      {returningUserData.type === 'authenticated' ? 'Continue Planning' : 'Get My Plan'}
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline" 
                      className="rounded-full h-14 px-8 text-lg"
                      onClick={() => {
                        setIsReturningUser(false);
                        setReturningUserData(null);
                      }}
                      data-testid="hero-start-fresh"
                    >
                      Start Fresh
                    </Button>
                  </div>
                </>
              ) : (
                /* New User Hero */
                <>
                  <Badge variant="secondary" className="mb-4 bg-[#4A5D4A]/10 text-[#4A5D4A] border-[#4A5D4A]/20">
                    <Sparkles className="h-3 w-3 mr-1" />
                    Weekly planning, simplified
                  </Badge>
                  <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] text-[#2D3A2D] mb-4">
                    Dinner, planned.<br />
                    Groceries, handled.
                  </h1>
                  <p className="text-lg text-[#5A6A5A] leading-relaxed mb-4 max-w-lg">
                    Choose how you want the week to feel. We&apos;ll build the meals and the grocery cart using ingredients you already buy.
                  </p>
                  
                  {/* Meal Kit Objection Killer */}
                  <p className="text-sm text-[#5A6A5A] mb-6">
                    No subscriptions. No meal kits. Just groceries you already trust.
                  </p>
                  
                  {/* Benefits List */}
                  <ul className="space-y-2 mb-8">
                    {benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center gap-2 text-[#3D4D3D]">
                        <benefit.icon className="h-5 w-5 text-[#4A5D4A]" />
                        <span>{benefit.text}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Primary CTA */}
                  <div className="flex flex-col items-start">
                    <Button 
                      size="lg" 
                      className="rounded-full h-14 px-8 text-lg btn-lift group bg-[#4A5D4A] hover:bg-[#3D4D3D] text-white"
                      onClick={() => setShowTeaser(true)}
                      data-testid="hero-plan-my-week"
                    >
                      Plan My Week
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                    <span className="text-xs text-[#5A6A5A] mt-2 ml-2">Free to start · No credit card</span>
                    <Link 
                      to="/demo" 
                      className="text-sm text-[#4A5D4A] hover:underline mt-3 ml-2"
                      data-testid="hero-see-demo"
                    >
                      Or see this week&apos;s plan →
                    </Link>
                  </div>
                </>
              )}
            </div>

            {/* Right: Teaser, Returning User Card, or Hero Image */}
            <div className="relative animate-slide-up stagger-2">
              {showTeaser ? (
                <Card className="p-6 shadow-2xl border-primary/20">
                  <PreSignupTeaser 
                    onSignup={handleTeaserSignup}
                    onComplete={() => {}}
                  />
                </Card>
              ) : isReturningUser && returningUserData?.pathDetails ? (
                /* Returning User - Show their path card */
                <Card className="p-8 shadow-2xl border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10">
                  <div className="text-center">
                    <div className={`w-20 h-20 rounded-full ${returningUserData.pathDetails.color} mx-auto mb-4 flex items-center justify-center`}>
                      {returningUserData.pathDetails.icon && (
                        <returningUserData.pathDetails.icon className="h-10 w-10 text-white" />
                      )}
                    </div>
                    <Badge variant="secondary" className="mb-3">Your Path</Badge>
                    <h3 className="text-2xl font-semibold mb-2">
                      {returningUserData.pathDetails.name}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {returningUserData.pathDetails.description}
                    </p>
                    {returningUserData.mealCount > 0 && (
                      <div className="bg-background/50 rounded-lg p-4 mb-4">
                        <p className="text-3xl font-bold text-primary">{returningUserData.mealCount}</p>
                        <p className="text-sm text-muted-foreground">meals planned</p>
                      </div>
                    )}
                    <div className="flex justify-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {returningUserData.pathDetails.time}
                      </span>
                      <span className="flex items-center gap-1">
                        <Utensils className="h-4 w-4" />
                        5 meals/week
                      </span>
                    </div>
                  </div>
                </Card>
              ) : (
                <>
                  <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                    <img 
                      src="https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg"
                      alt="Fresh healthy meal preparation"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {/* Floating Stats Card */}
                  <div className="absolute -bottom-6 -left-6 bg-card rounded-xl shadow-lg p-4 border border-border/50 animate-fade-in stagger-3">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Clock className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Plan your week in</p>
                        <p className="font-semibold">Under 2 minutes</p>
                      </div>
                    </div>
                  </div>
                  {/* Floating Users Card */}
                  <div className="absolute -top-4 -right-4 bg-card rounded-xl shadow-lg p-4 border border-border/50 animate-fade-in stagger-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-accent/30 flex items-center justify-center">
                        <Users className="h-5 w-5 text-accent-foreground" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Trusted by</p>
                        <p className="font-semibold">10K+ planners</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Path Cards Section - Below the Fold */}
      <section className="py-16 bg-muted/30" data-testid="path-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-3 font-['Playfair_Display']">
              Choose Your Path
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Each path is a curated week of meals. Pick one and we&apos;ll handle the rest.
            </p>
          </div>
          
          {/* Path Cards Grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {pathPreviews.map((path) => (
              <Card 
                key={path.id}
                className="cursor-pointer hover:border-primary/50 hover:shadow-lg transition-all group"
                onClick={() => handlePathSelect(path.id)}
                data-testid={`landing-path-${path.id}`}
              >
                <CardContent className="p-5 text-center">
                  <div className={`w-14 h-14 rounded-full ${path.color} mx-auto mb-3 flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <path.icon className="h-7 w-7 text-white" />
                  </div>
                  <p className="font-medium mb-1">{path.name}</p>
                  <p className="text-xs text-muted-foreground mb-2">{path.desc}</p>
                  <Badge variant="outline" className="text-xs">
                    <Clock className="h-3 w-3 mr-1" />
                    {path.time}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* CTA below paths */}
          <div className="text-center mt-8">
            <p className="text-sm text-muted-foreground mb-3">Not sure which to pick?</p>
            <Button 
              variant="outline" 
              className="rounded-full"
              onClick={() => setShowTeaser(true)}
            >
              Help Me Choose
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
            <p className="text-xs text-[#5A6A5A] mt-6 italic">
              Swap meals, ingredients, or skip weeks anytime.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8">
            {[
              { value: '2 minutes', label: 'to plan your week' },
              { value: '5 flexible', label: 'dinners, no rigid recipes' },
              { value: '1 complete', label: 'cart, ready for checkout' },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-primary font-['Playfair_Display']">
                  {stat.value}
                </p>
                <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Simplified */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3 text-[#2D3A2D]">
              How It Works
            </h2>
            <p className="text-[#5A6A5A]">Three steps to dinner freedom</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                step: '1', 
                title: 'Choose a path', 
                desc: 'How you want the week to feel — healthy, quick, family-friendly, or budget.',
                icon: Sparkles 
              },
              { 
                step: '2', 
                title: 'Get a weekly plan', 
                desc: '5 flexible dinners with overlapping ingredients. Swap anything.',
                icon: Calendar 
              },
              { 
                step: '3', 
                title: 'Send to your store', 
                desc: 'One complete cart, ready for checkout.',
                icon: ShoppingCart 
              }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#4A5D4A]/10 mx-auto mb-4 flex items-center justify-center">
                  <item.icon className="h-8 w-8 text-[#4A5D4A]" />
                </div>
                <div className="text-4xl font-bold text-[#4A5D4A]/20 mb-2">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold mb-2 text-[#2D3A2D]">{item.title}</h3>
                <p className="text-sm text-[#5A6A5A] max-w-xs mx-auto">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-[#5A6A5A] mb-4">Works with your favorite stores</p>
            <div className="flex justify-center gap-8 text-[#4A5D4A]/60">
              {/* Partner/store logos would go here */}
              <div className="flex items-center gap-2 text-lg font-medium">
                <ShoppingCart className="h-5 w-5" /> Instacart
              </div>
              <div className="flex items-center gap-2 text-lg font-medium">
                <ShoppingCart className="h-5 w-5" /> Walmart
              </div>
              <div className="flex items-center gap-2 text-lg font-medium">
                <ShoppingCart className="h-5 w-5" /> Target
              </div>
              <div className="flex items-center gap-2 text-lg font-medium">
                <ShoppingCart className="h-5 w-5" /> Amazon Fresh
              </div>
            </div>
            <p className="text-xs text-[#5A6A5A] mt-6">
              Pro unlocks direct store checkout and advanced planning. <Link to="/subscription" className="underline text-[#4A5D4A]">Upgrade anytime</Link>
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-primary/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-4 font-['Playfair_Display']">
            Ready to get your week handled?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Pick a path, get a plan, send to your store. That&apos;s it.
          </p>
          <div className="flex flex-col items-center">
            <Button 
              size="lg" 
              className="rounded-full h-14 px-10 text-lg"
              onClick={() => navigate('/auth?signup=true')}
              data-testid="final-cta"
            >
              Plan My Week
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <span className="text-sm text-muted-foreground mt-4">
              No subscriptions · No meal kits · No credit card
            </span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-primary" />
              <span className="font-medium">Carted</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2025 Carted. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
